import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCallback, useContext, useId, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Expander from 'components/utils/Expander'
import DropdownChevron from '../../../../LuxKit/Dropdown/DropdownChevron'
import Clickable from 'components/Common/Clickable'
import { formToObject } from 'lib/forms/formToObject'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setAgentPayoutDetailsAndMarkAsOnboarded } from 'actions/AgentHubActions'
import type { AgentHubSetPayoutDetailsBody } from 'agentHub/api/requestSetAgentHubPayoutDetails'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import config from 'constants/config'
import AgentHubSetupPaymentDetailsVerificationRow from './AgentHubSetupPaymentDetailsVerificationRow'
import TextInput from 'components/Common/Form/Input/TextInput'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import AgentHubAccountSetupStepIndicator from 'agentHub/components/AgentHubAccountSetupStepIndicator/AgentHubAccountSetupStepIndicator'
import { useIntl } from 'react-intl'

const FullWidthTextInput = styled(TextInput)`
  grid-column: 1 / -1;
`

const AgentHubPaymentVerificationContainer = styled(Group)`
  min-height: ${rem(312)};
`

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const StyledGroup = styled(Group)`
  padding: ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const Trigger = styled(Clickable)`
  width: 100%;
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
  display: grid;
  gap: ${rem(16)};
  grid-template: "content side" auto / 1fr auto;
  align-items: center;
  padding: ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};

  &.expanded {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.neutral.default.five};
    outline-offset: 1px;
  }
`

const TriggerSideArea = styled(Group)`
  grid-area: side;
  justify-self: end;
`

const TriggerContentArea = styled(VerticalSpacer)`
  grid-area: content;
`

export interface PayoutDetailsFormData {
  accountName: string
  accountNumber: string
  bsb?: string
  sortCode?: string
  swift?: string
}

interface Props {
  onSubmitSetup: () => void
  onSetupLater: () => void
}

interface FormField {
  label: string
  name: keyof PayoutDetailsFormData
  placeholder: string
  invalidErrorMessage: string
  required: boolean
  fullWidth?: boolean
}

function AgentHubSetupCommissionPayoutStep({ onSubmitSetup, onSetupLater }: Props) {
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()

  const accountNameLabel = intl.formatMessage({
    id: 'NlcYZo_Xt-m4ESll7Y0l',
    defaultMessage: 'Account name',
    description: 'Account name label in commission payout setup form',
  })

  const accountNumberLabel = intl.formatMessage({
    id: 'lGzP8yZvbrxefVpDm6x0',
    defaultMessage: 'Account number',
    description: 'Account number label in commission payout setup form',
  })

  const REGION_FIELDS: Record<string, Array<FormField>> = useMemo(() => ({
    AU: [
      {
        label: intl.formatMessage({
          id: '6l3tQ19pxcNczj3PoT2M',
          defaultMessage: 'BSB',
          description: 'BSB input label in commission payout setup form for Australian region',
        }),
        name: 'bsb',
        placeholder: intl.formatMessage({
          id: '6dq0-CZeJoVwh3ftIyE_',
          defaultMessage: 'Enter BSB',
          description: 'BSB input placeholder in commission payout setup form',
        }),
        invalidErrorMessage: intl.formatMessage({
          id: '21RsTMMIXw4VOHZTEZFo',
          defaultMessage: 'Please enter a valid 6-digit BSB number',
          description: 'Validation error when BSB number is invalid in commission payout form',
        }),
        required: true,
      },
    ],
    NZ: [
      {
        label: intl.formatMessage({
          id: 'OT0VesyFPR8OYAWCWjX1',
          defaultMessage: 'SWIFT',
          description: 'SWIFT code input label in commission payout setup form for New Zealand region',
        }),
        name: 'swift',
        placeholder: intl.formatMessage({
          id: 'UapFXRS_Gy_fbE4-25cw',
          defaultMessage: 'Enter SWIFT',
          description: 'SWIFT code input placeholder in commission payout setup form',
        }),
        invalidErrorMessage: intl.formatMessage({
          id: 'G6psuyHyXfjc1C6z18HN',
          defaultMessage: 'Please enter a valid SWIFT code',
          description: 'Validation error when SWIFT code is invalid in commission payout form',
        }),
        required: true,
      },
    ],
    GB: [
      {
        label: intl.formatMessage({
          id: 'BWOz5-HtcmjDQf3NJJQu',
          defaultMessage: 'Sort code',
          description: 'Sort code input label in commission payout setup form for UK region',
        }),
        name: 'sortCode',
        placeholder: intl.formatMessage({
          id: '2JIKSPp6ikFq18sqPTq4',
          defaultMessage: 'Enter sort code',
          description: 'Sort code input placeholder in commission payout setup form',
        }),
        invalidErrorMessage: intl.formatMessage({
          id: 'MaJ7Rsg7S5JRMayO1lkf',
          defaultMessage: 'Please enter a valid sort code',
          description: 'Validation error when sort code is invalid in commission payout form',
        }),
        required: true,
      },
    ],
  }), [intl])
  const isVerificationEnabled = config.AGENT_HUB_PAYMENT_DETAIL_SUBMISSION_ON_SIGNUP_VERIFICATION_ENABLED

  const isSavingAgencyPayoutDetails = useAppSelector((state) => state.agentHub.isSavingAgencyPayoutDetails)
  const savingAgencyPayoutDetailsError = useAppSelector((state) => state.agentHub.savingAgencyPayoutDetailsError)
  const isOnboardingAgency = useAppSelector((state) => state.agentHub.onboardingAgency)
  const account = useAppSelector((state) => state.agentHub.agentHubAccount.account)

  const [showForm, setShowForm] = useState(false)
  const [verifyingData, setVerifyingData] = useState<PayoutDetailsFormData>()
  const onToggleForm = useCallback(() => setShowForm(showForm => !showForm), [])

  const submitAgentPaymentForm = useCallback((formData: PayoutDetailsFormData) => {
    function getPayoutDetails() {
      return formData[REGION_FIELDS[currentRegionCode]?.[0]?.name] ?? {}
    }

    const requestBody: AgentHubSetPayoutDetailsBody = {
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      ...getPayoutDetails(),
    }

    dispatch(setAgentPayoutDetailsAndMarkAsOnboarded(requestBody, account?.agentId || ''))
    onSubmitSetup()
  }, [account?.agentId, currentRegionCode, dispatch, onSubmitSetup, REGION_FIELDS])

  const onPaymentSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formData = formToObject<PayoutDetailsFormData>(event.currentTarget)

    if (isVerificationEnabled) {
      setVerifyingData(formData)
    } else {
      submitAgentPaymentForm(formData)
    }
  }

  const onEditDetails = useCallback(() => {
    setVerifyingData(undefined)
  }, [])

  const onVerificationSubmit = useCallback(() => {
    if (!verifyingData) {
      return
    }
    submitAgentPaymentForm(verifyingData)
  }, [submitAgentPaymentForm, verifyingData])

  const formId = useId()

  return <>
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <AgentHubAccountSetupStepIndicator currentStep="commissionsPayout" />
          <div>
            <LoadingIndicator visible={isSavingAgencyPayoutDetails || isOnboardingAgency} floating />
            <div hidden={Boolean(verifyingData)}>
              <form onSubmit={onPaymentSubmit} id={formId}>
                <VerticalSpacer gap={4}>
                  <HeadingTextBlock variant="heading5">
                    {intl.formatMessage({
                      id: 'pYIG-ujFilMGmHyKKZap',
                      defaultMessage: 'Want the option to pay gross?',
                      description: 'Heading for commission payout setup section in agent hub account setup',
                    })}
                  </HeadingTextBlock>
                  <BodyTextBlock variant="medium">
                    {intl.formatMessage({
                      id: '_xdHy9vFHIlQy6Sge87S',
                      defaultMessage: 'Receive commissions after your client completes their trip. Link a bank account to set up this feature. Please note that the default option is to pay net upon checkout.',
                      description: 'Description for commission payout setup explaining gross pay option',
                    })}
                  </BodyTextBlock>
                </VerticalSpacer>

                {!!savingAgencyPayoutDetailsError && <MessageBanner kind="critical">
                  {savingAgencyPayoutDetailsError}
                </MessageBanner>}

                <Trigger onClick={onToggleForm} className={showForm ? 'expanded' : ''}>
                  <TriggerContentArea gap={4}>
                    <HeadingTextBlock startIcon={<LineUniversityIcon />} variant="heading6">
                      {intl.formatMessage({
                        id: 'q7uFaR34wE5Z1bCDZE0l',
                        defaultMessage: 'Set up commission payout',
                        description: 'Heading for expandable commission payout section in agent hub setup',
                      })}
                    </HeadingTextBlock>
                    <BodyTextBlock variant="medium">
                      {intl.formatMessage({
                        id: 'cLdk3DVOpBieIABaD99m',
                        defaultMessage: 'Commissions will be paid out into your designated bank account',
                        description: 'Description under commission payout heading in agent hub setup',
                      })}
                    </BodyTextBlock>
                  </TriggerContentArea>

                  <TriggerSideArea direction="horizontal" verticalAlign="center" gap={12}>
                    <DropdownChevron size="M" direction={showForm ? 'up' : 'down'} />
                  </TriggerSideArea>
                </Trigger>

                <Expander expanded={showForm}>
                  <StyledGroup direction="vertical" gap={24}>
                    <InputGrid>
                      <FullWidthTextInput
                        label={accountNameLabel}
                        name="accountName"
                        placeholder={intl.formatMessage({
                          id: '5fM5RoSTbdmXrPxPHlp_',
                          defaultMessage: 'Enter account name',
                          description: 'Placeholder for account name input in commission payout setup form',
                        })}
                        invalidErrorMessage={intl.formatMessage({
                          id: 'Hca_2ZMtrQ1MYmx77wea',
                          defaultMessage: 'Account name is required',
                          description: 'Validation error when account name is empty in commission payout form',
                        })}
                        required
                        content={verifyingData?.accountName ?? ''}
                      />
                      {REGION_FIELDS[currentRegionCode]?.map(field => <TextInput
                        label={field.label}
                        content={verifyingData?.[field.name] ?? ''}
                        key={field.name}
                        name={field.name}
                        placeholder={field.placeholder}
                        invalidErrorMessage={field.invalidErrorMessage}
                        required
                      />)}
                      <TextInput
                        label={accountNumberLabel}
                        name="accountNumber"
                        placeholder={intl.formatMessage({
                          id: 'p2IxPs4HnqotHrBxWF--',
                          defaultMessage: 'Enter account number',
                          description: 'Placeholder for account number input in commission payout setup form',
                        })}
                        invalidErrorMessage={intl.formatMessage({
                          id: 'aLopmsJsmYpI0CbKxfhp',
                          defaultMessage: 'Account number is required',
                          description: 'Validation error when account number is empty in commission payout form',
                        })}
                        required
                        content={verifyingData?.accountNumber ?? ''}
                      />
                    </InputGrid>
                    <CaptionTextBlock variant="large">
                      {intl.formatMessage({
                        id: '1nTr0G4Oz5cF-HGgZ04V',
                        defaultMessage: 'Commissions are scheduled to be paid into your linked account within 30 days following the completion of your client’s trip.',
                        description: 'Caption explaining commission payout schedule in agent hub setup',
                      })}
                    </CaptionTextBlock>
                  </StyledGroup>
                </Expander>
              </form>
            </div>
            {isVerificationEnabled && verifyingData && <Group direction="vertical" gap={24}>
              <AgentHubPaymentVerificationContainer direction="vertical" gap={16}>
                <HeadingTextBlock variant="heading5">
                  {intl.formatMessage({
                    id: 'afXNYsfYrRDNu8iPkUT3',
                    defaultMessage: 'Confirm your account details',
                    description: 'Heading for bank details verification step in commission payout setup',
                  })}
                </HeadingTextBlock>
                <MessageBanner
                  kind="warning"
                  density="compact"
                  icon={<LineExclamationTriangleIcon />}
                  heading={intl.formatMessage({
                    id: 'PW2h58zjUHOFhx9k1GxS',
                    defaultMessage: 'Double-check your bank details below.',
                    description: 'Warning banner heading in bank details verification step',
                  })}
                >
                  {intl.formatMessage({
                    id: 'qQjHai-Elxesg-5ppsAQ',
                    defaultMessage: 'Incorrect information may lead to delayed or failed payments.',
                    description: 'Warning banner body in bank details verification step',
                  })}
                </MessageBanner>
                <Group direction="vertical" gap={4}>
                  <AgentHubSetupPaymentDetailsVerificationRow
                    label={accountNameLabel}
                    content={verifyingData.accountName}
                  />
                  {REGION_FIELDS[currentRegionCode]?.map(field => <AgentHubSetupPaymentDetailsVerificationRow
                    label={field.label}
                    content={verifyingData[field.name] || ''}
                    key={field.name}
                  />)}
                  <AgentHubSetupPaymentDetailsVerificationRow
                    label={accountNumberLabel}
                    content={verifyingData.accountNumber}
                  />
                </Group>
              </AgentHubPaymentVerificationContainer>
            </Group>}
          </div>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    {!verifyingData && <ModalFooter
      actionsLayout="auto"
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'TZZfWpXjZYn5KraxIRVz',
          defaultMessage: 'Complete payment preferences',
          description: 'Primary button to submit commission payout form in agent hub setup',
        }),
        type: 'submit',
        disabled: isSavingAgencyPayoutDetails || isOnboardingAgency,
        form: formId,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'luVm3vIMu9NZJhE7IfP2',
          defaultMessage: 'Set up later',
          description: 'Secondary button to skip commission payout setup in agent hub',
        }),
        onClick: onSetupLater,
        disabled: isSavingAgencyPayoutDetails || isOnboardingAgency,
      }}
    />}
    {verifyingData && isVerificationEnabled && <ModalFooter
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'dy3MwQY5LW_-dkOXtD6n',
          defaultMessage: 'Confirm details',
          description: 'Primary button to confirm bank details in commission payout verification step',
        }),
        type: 'submit',
        disabled: isSavingAgencyPayoutDetails || isOnboardingAgency,
        onClick: onVerificationSubmit,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'mAhei0b4eTjlJGJc4lFS',
          defaultMessage: 'Edit',
          description: 'Secondary button to go back and edit bank details in commission payout verification step',
        }),
        onClick: onEditDetails,
        disabled: isSavingAgencyPayoutDetails || isOnboardingAgency,
      }}
    />}
  </>
}

export default AgentHubSetupCommissionPayoutStep
